import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IBestDataset, DownloadInfoItem } from '../../../../types/analytics';
import { datasetType, ITEM, TabKey } from '../../../../utils/common';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { MarkerItem } from '../../../../types/home';
import { DatasetType } from '../../../../types/marker';

interface useAnalyticsCommonInputProps {
  downloadItems: DownloadInfoItem[];
  category: string | string[];
  coordinates: number[];
  previewDatasetId: string;
  point?: any;
  enrichedMetadata: IEnrichedMetadata[];
  showMesh: boolean;
  onMeshControl: (value: string) => void;
  updateMarker: (id: string, data: any) => void;
  markers: MarkerItem[];
  onChangeActiveKey: (item: any, key: TabKey) => void;
  setActiveDatasetKey: (
    markerId: string,
    datasetKey: DatasetType,
    newActiveDataset: string
  ) => void;
}

export const useAnalyticsCommonInput = (
  props: useAnalyticsCommonInputProps
) => {
  const navigate = useNavigate();
  const [temporaryDatasetName, setTemporaryDatasetName] = useState('');
  const [isChangeDatasetWarningVisible, setIsChangeDatasetWarningVisible] =
    useState(false);

  const categoryDataset = useMemo(() => {
    const selectedDataset: IBestDataset[] = [];

    props.downloadItems.forEach((element) => {
      const options = element.options.map((item) => {
        return {
          id: item.id,
          label: datasetType[element.radioKey],
          value: item.value,
        };
      });
      selectedDataset.push(...options);
    });

    return selectedDataset.length > 0 ? selectedDataset : [];
  }, [props.downloadItems, props.category, props.coordinates]);

  const onDatasetValueChange = useCallback(
    (item: DownloadInfoItem) => {
      if (!props.point?.downloadPage) return;

      const idx = props.point.downloadPage.findIndex(
        (d: any) => d.radioKey === item.radioKey
      );

      if (idx < 0) return;

      const updatedDownloadPage = [...props.point.downloadPage];
      updatedDownloadPage[idx] = item;
      props.updateMarker(props.point.id, { downloadPage: updatedDownloadPage });
    },
    [props.point, props.updateMarker]
  );

  const changeDataset = useCallback(
    (newDataset: string) => {
      if (!props.point?.downloadPage) return;

      const datasetKey = Object.entries(props.point?.allDataset || {})
        .filter(([key]) => !key.includes('_Default'))
        .find(([key, datasets]) => {
          if (Array.isArray(datasets)) {
            return datasets.includes(newDataset);
          }
          return datasets === newDataset;
        })?.[0];

      const obj = Object.assign(
        {},
        props.point?.downloadPage.find((item: { radioKey: string }) => {
          return item.radioKey === datasetKey;
        }),
        { datasetId: newDataset }
      );

      onDatasetValueChange(obj);
      props.setActiveDatasetKey(
        props.point!.id,
        datasetKey as DatasetType,
        newDataset
      );
    },
    [
      props.point,
      props.category,
      props.showMesh,
      props.onMeshControl,
      props.updateMarker,
      onDatasetValueChange,
      navigate,
    ]
  );

  const onDatasetChange = useCallback(
    (value: string) => {
      const datasetTask = props.enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === props.previewDatasetId &&
          task.CoordinatesString === props.coordinates.join()
      );

      if (datasetTask === undefined) {
        changeDataset(value);
      } else {
        setTemporaryDatasetName(value);
        setIsChangeDatasetWarningVisible(true);
      }
    },
    [props.enrichedMetadata]
  );

  const handleChangeDatasetWarningOk = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    changeDataset(temporaryDatasetName ?? '');
    setTemporaryDatasetName('');
  }, [
    temporaryDatasetName,
    setIsChangeDatasetWarningVisible,
    changeDataset,
    setTemporaryDatasetName,
  ]);

  const handleChangeDatasetWarningCancel = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    setTemporaryDatasetName('');
  }, [setIsChangeDatasetWarningVisible, setTemporaryDatasetName]);

  const hasExistingDatasetTask = useMemo(() => {
    return props.enrichedMetadata.some(
      (task: IEnrichedMetadata) =>
        task.DatasetId === props.previewDatasetId &&
        task.CoordinatesString === props.coordinates.join()
    );
  }, [props.enrichedMetadata, props.previewDatasetId, props.coordinates]);

  const pointOptions = useMemo(() => {
    const points = props.markers?.map((item) => ({
      label: item.label,
      value: item.id,
    }));
    return points ?? [];
  }, [props.markers]);

  const handleChangePoint = (item: any) => {
    props.onChangeActiveKey(item, ITEM.ANALYTICS);
  };

  return {
    categoryDataset,
    pointOptions,
    handleChangePoint,
    hasExistingDatasetTask,
    onDatasetChange,
    onDatasetValueChange,
    changeDataset,
    handleChangeDatasetWarningOk,
    handleChangeDatasetWarningCancel,
    isChangeDatasetWarningVisible,
  };
};
